import { useEffect, useState } from 'react';
import { FilePlus2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { api, buildQuery } from '../../lib/api';
import type { Report, ReportAttachment } from '../../types';
import { formatDate, formatDateTime } from '../../lib/format';
import { Spinner, EmptyState, StatusBadge, Avatar } from '../../components/ui';
import ReportDetailModal from '../../components/ReportDetailModal';
import ReportAttachments from '../../components/ReportAttachments';
import MonthlyReportForm, { parseMonthlyReport } from './MonthlyReportForm';
import ManagerReportFormModal, { MANAGER_REPORT_TYPES, MANAGER_TYPE_LABELS } from './ReportFormModal';
import { parseDailyReport } from '../../components/DailyReportForm';
import { useAuth } from '../../lib/auth';
import { useEstates } from '../../lib/useEstates';

type Tab = 'staff' | 'mine';

function MonthlySectionView({ label, lines, attachmentIndices, attachments, onDeleted }: {
  label: string;
  lines: string[];
  attachmentIndices: number[];
  attachments: ReportAttachment[];
  onDeleted?: (id: number) => void;
}) {
  const [open, setOpen] = useState(true);
  const sectionAttachments = attachmentIndices.map((i) => attachments[i]).filter(Boolean);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
          {label}
          {lines.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />}
          {sectionAttachments.length > 0 && (
            <span className="text-xs text-gray-400">({sectionAttachments.length} file{sectionAttachments.length !== 1 ? 's' : ''})</span>
          )}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 py-3 bg-white">
          {lines.length > 0 ? (
            <div className="space-y-1 mb-3">
              {lines.map((line, i) => {
                const colonIdx = line.indexOf(':');
                if (colonIdx > 0 && colonIdx < 80) {
                  const key = line.slice(0, colonIdx).trim();
                  const val = line.slice(colonIdx + 1).trim();
                  return (
                    <div key={i} className="flex gap-2 text-sm">
                      <span className="font-medium text-gray-700 shrink-0">{key}:</span>
                      <span className="text-gray-600">{val}</span>
                    </div>
                  );
                }
                return <p key={i} className="text-sm text-gray-600">{line}</p>;
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic mb-3">No details provided.</p>
          )}
          {sectionAttachments.length > 0 && (
            <ReportAttachments attachments={sectionAttachments} onDeleted={onDeleted} />
          )}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const { estates } = useEstates();
  const [tab, setTab] = useState<Tab>('staff');
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [facilityFilter, setFacilityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [selected, setSelected] = useState<Report | null>(null);

  useEffect(() => {
    setLoading(true);
    const query: Record<string, string> = { facility: facilityFilter };
    if (typeFilter) query.report_type = typeFilter;
    if (tab === 'mine' && user) query.user_id = String(user.id);
    api
      .get<Report[]>(`/reports${buildQuery(query)}`)
      .then((rows) => {
        if (tab === 'staff') setReports(rows.filter((r) => r.user_role === 'staff'));
        else setReports(rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab, facilityFilter, typeFilter, user]);

  const handleSubmitted = () => {
    setFacilityFilter('');
    setTypeFilter('');
    setLoading(true);
    const query: Record<string, string> = {};
    if (tab === 'mine' && user) query.user_id = String(user.id);
    api
      .get<Report[]>(`/reports${buildQuery(query)}`)
      .then((rows) => {
        if (tab === 'staff') setReports(rows.filter((r) => r.user_role === 'staff'));
        else setReports(rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const openReport = (r: Report) => {
    setSelected(r);
    api
      .get<Report>(`/reports/${r.id}`)
      .then((fresh) => setSelected(fresh))
      .catch(() => {});
  };

  const removeAttachment = (reportId: number, attachmentId: number) => {
    setReports((prev) =>
      prev.map((r) => r.id === reportId
        ? { ...r, attachments: r.attachments.filter((a) => a.id !== attachmentId) }
        : r)
    );
  };

  const renderReport = (r: Report) => {
    const isMonthlyReport = r.report_type === 'monthly' && r.heading === 'Monthly Report';
    const isDailyReport = r.report_type === 'daily' && r.heading === 'Daily Report';
    const sections = isMonthlyReport
      ? parseMonthlyReport(r.content, r.attachments)
      : isDailyReport
        ? parseDailyReport(r.content, r.attachments)
        : null;

    if (!sections || sections.length === 0) {
      return (
        <button
          key={r.id}
          type="button"
          onClick={() => openReport(r)}
          className="bg-white p-5 rounded-lg shadow-lg text-left hover:shadow-xl hover:ring-1 hover:ring-primary/40 transition w-full"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="flex items-center">
              <Avatar src={r.image_url} name={r.user_name} className="w-8 h-8 mr-3" />
              <div>
                <p className="font-medium">{r.user_name}</p>
                <p className="text-xs text-gray-500 capitalize">{r.user_role}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={r.report_type} />
              {r.facility && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{r.facility}</span>
              )}
              <span className="text-sm text-gray-500">{formatDate(r.report_date)}</span>
            </div>
          </div>
          {r.heading && <h3 className="font-semibold text-gray-800 mb-1">{r.heading}</h3>}
          <p className="text-sm text-gray-600 whitespace-pre-line mb-2 line-clamp-3">{r.content}</p>
          <div className="flex items-center text-xs text-gray-400">
            <Clock className="h-3.5 w-3.5 mr-1" />
            Uploaded {formatDateTime(r.created_at)} · Click to open
          </div>
        </button>
      );
    }

    return (
      <div key={r.id} className="bg-white p-5 rounded-lg shadow-lg">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center">
            <Avatar src={r.image_url} name={r.user_name} className="w-8 h-8 mr-3" />
            <div>
              <p className="font-medium">{r.user_name}</p>
              <p className="text-xs text-gray-500 capitalize">{r.user_role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={r.report_type} />
            {r.facility && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{r.facility}</span>
            )}
            <span className="text-sm text-gray-500">{formatDate(r.report_date)}</span>
          </div>
        </div>
        {r.heading && <h3 className="font-semibold text-gray-800 mb-1">{r.heading}</h3>}
        <div className="flex items-center text-xs text-gray-400 mb-2">
          <Clock className="h-3.5 w-3.5 mr-1" />
          Uploaded {formatDateTime(r.created_at)}
        </div>
        <div className="space-y-2">
          {sections.map((sec) => (
            <MonthlySectionView
              key={sec.label}
              label={sec.label}
              lines={sec.lines}
              attachmentIndices={sec.attachmentIndices}
              attachments={r.attachments}
              onDeleted={(attId) => removeAttachment(r.id, attId)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Site Report</h1>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSiteModalOpen(true)}
            className="px-4 py-2 bg-primary text-black rounded-lg flex items-center space-x-2 hover:bg-primary-dark text-sm"
          >
            <FilePlus2 className="h-4 w-4" /> Site Report
          </button>
          <button
            onClick={() => setModalOpen(true)}
            className="px-4 py-2 border border-primary text-primary rounded-lg flex items-center space-x-2 hover:bg-primary/5 text-sm"
          >
            <FilePlus2 className="h-4 w-4" /> Monthly Report
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setTab('staff')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === 'staff' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Staff Reports
        </button>
        <button
          onClick={() => setTab('mine')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === 'mine' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          My Reports
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={facilityFilter}
          onChange={(e) => setFacilityFilter(e.target.value)}
        >
          <option value="">All facilities</option>
          {estates.map((f) => (
            <option key={f.name} value={f.name}>{f.name}</option>
          ))}
        </select>
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All report types</option>
          {MANAGER_REPORT_TYPES.map((t) => (
            <option key={t} value={t}>{MANAGER_TYPE_LABELS[t] || t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading reports..." />
      ) : reports.length === 0 ? (
        <EmptyState message="No reports found." />
      ) : (
        <div className="grid gap-4">
          {reports.map(renderReport)}
        </div>
      )}

      <ManagerReportFormModal
        open={siteModalOpen}
        onClose={() => setSiteModalOpen(false)}
        onSubmitted={handleSubmitted}
      />

      <MonthlyReportForm
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={handleSubmitted}
      />

      <ReportDetailModal
        report={selected}
        onClose={() => setSelected(null)}
        onAttachmentDeleted={(attId) => {
          setSelected((cur) =>
            cur ? { ...cur, attachments: cur.attachments.filter((a) => a.id !== attId) } : cur
          );
          setReports((prev) =>
            prev.map((r) =>
              r.id === selected?.id ? { ...r, attachments: r.attachments.filter((a) => a.id !== attId) } : r
            )
          );
        }}
      />
    </div>
  );
}
